/**
 * Utility functions for Orchestrator Tree validation and management
 */

import type { TreeNode, ConversationTree } from "@/components/reasoning/types";

/**
 * Recursively count all future nodes in a tree branch
 */
/**
 * Check if a node status represents a future/prediction node.
 * Must match the check in treeLayout.ts for consistency.
 */
function isFutureStatus(status: string | undefined): boolean {
  return status === "future" || status === "predicted" || status === "recommended";
}

function countFutureNodesRecursive(node: TreeNode): number {
  let count = 0;

  if (isFutureStatus(node.status)) {
    count = 1;
  }

  for (const child of node.children || []) {
    count += countFutureNodesRecursive(child);
  }

  return count;
}

/**
 * Find the current node in the tree
 */
function findCurrentNode(node: TreeNode, currentNodeId: string): TreeNode | null {
  if (node.id === currentNodeId) {
    return node;
  }

  for (const child of node.children || []) {
    const found = findCurrentNode(child, currentNodeId);
    if (found) return found;
  }

  return null;
}

/**
 * Count future nodes from the current position
 *
 * Requirement: focused top-fanout prediction shape = 10 total minimum
 * - Level 1: 3 immediate options
 * - Level 2: 3 children under the highest-probability option, 1 under each other option
 * - Levels 3-4: a single-child continuation under the highest-probability top child
 *
 * @param tree - The conversation tree
 * @returns Number of future nodes from current position
 */
export function countFutureNodesFromCurrent(tree: ConversationTree | null): number {
  if (!tree) return 0;

  const currentNode = findCurrentNode(tree.rootNode, tree.currentNodeId);
  if (!currentNode) return 0;

  // Count future nodes in all children
  let futureCount = 0;
  for (const child of currentNode.children) {
    futureCount += countFutureNodesRecursive(child);
  }

  return futureCount;
}

/**
 * Validate tree has minimum required future nodes
 *
 * @param tree - The conversation tree
 * @param minNodes - Minimum required future nodes (default: 10)
 * @returns True if tree has enough future nodes
 */
export function hasMinimumFutureNodes(
  tree: ConversationTree | null,
  minNodes: number = 10
): boolean {
  const count = countFutureNodesFromCurrent(tree);
  return count >= minNodes;
}

/**
 * Calculate how many more future nodes are needed
 *
 * @param tree - The conversation tree
 * @param minNodes - Minimum required future nodes (default: 10)
 * @returns Number of additional future nodes needed (0 if sufficient)
 */
export function getFutureNodesDeficit(
  tree: ConversationTree | null,
  minNodes: number = 10
): number {
  const count = countFutureNodesFromCurrent(tree);
  const deficit = minNodes - count;
  return Math.max(0, deficit);
}

function getImmediateFutureChildren(node: TreeNode): TreeNode[] {
  return (node.children || []).filter((child) => isFutureStatus(child.status));
}

function getMaxFutureDepthFromNode(node: TreeNode, depth: number): number {
  if (!isFutureStatus(node.status)) return 0;

  const childDepths = getImmediateFutureChildren(node).map((child) =>
    getMaxFutureDepthFromNode(child, depth + 1)
  );

  return Math.max(depth, ...childDepths);
}

function getMaxFutureChildrenUnderNode(node: TreeNode): number {
  if (!isFutureStatus(node.status)) return 0;

  const children = getImmediateFutureChildren(node);
  const childMaxes = children.map(getMaxFutureChildrenUnderNode);
  return Math.max(children.length, ...childMaxes);
}

/**
 * Summarize whether future predictions match the focused shape:
 * 3 primary options, 3 children under the top primary, one child under each
 * non-top primary, and two extra single steps under the best top child.
 */
export function getFuturePredictionShape(tree: ConversationTree | null): {
  primaryCount: number;
  primariesWithChild: number;
  topPrimaryChildCount: number;
  nonTopPrimariesWithChild: number;
  topPathDepth: number;
  topBestChildPathDepth: number;
  topPathExtraSteps: number;
  maxChildrenUnderPrediction: number;
  total: number;
} {
  if (!tree) {
    return {
      primaryCount: 0,
      primariesWithChild: 0,
      topPrimaryChildCount: 0,
      nonTopPrimariesWithChild: 0,
      topPathDepth: 0,
      topBestChildPathDepth: 0,
      topPathExtraSteps: 0,
      maxChildrenUnderPrediction: 0,
      total: 0,
    };
  }

  const currentNode = findCurrentNode(tree.rootNode, tree.currentNodeId);
  if (!currentNode) {
    return {
      primaryCount: 0,
      primariesWithChild: 0,
      topPrimaryChildCount: 0,
      nonTopPrimariesWithChild: 0,
      topPathDepth: 0,
      topBestChildPathDepth: 0,
      topPathExtraSteps: 0,
      maxChildrenUnderPrediction: 0,
      total: 0,
    };
  }

  const primaryPredictions = getImmediateFutureChildren(currentNode);
  const topPrimary = primaryPredictions.reduce<TreeNode | null>((best, candidate) => {
    if (!best) return candidate;
    return (candidate.probability ?? 0) > (best.probability ?? 0) ? candidate : best;
  }, null);

  const topPrimaryChildren = topPrimary ? getImmediateFutureChildren(topPrimary) : [];
  const topBestChild = topPrimaryChildren.reduce<TreeNode | null>((best, candidate) => {
    if (!best) return candidate;
    return (candidate.probability ?? 0) > (best.probability ?? 0) ? candidate : best;
  }, null);
  const topPathDepth = topPrimary ? getMaxFutureDepthFromNode(topPrimary, 1) : 0;
  const topBestChildPathDepth = topBestChild
    ? getMaxFutureDepthFromNode(topBestChild, 2)
    : 0;

  return {
    primaryCount: primaryPredictions.length,
    primariesWithChild: primaryPredictions.filter(
      (primary) => getImmediateFutureChildren(primary).length >= 1
    ).length,
    topPrimaryChildCount: topPrimaryChildren.length,
    nonTopPrimariesWithChild: primaryPredictions.filter(
      (primary) =>
        primary !== topPrimary && getImmediateFutureChildren(primary).length >= 1
    ).length,
    topPathDepth,
    topBestChildPathDepth,
    topPathExtraSteps: Math.max(0, topBestChildPathDepth - 2),
    maxChildrenUnderPrediction: Math.max(
      0,
      ...primaryPredictions.map(getMaxFutureChildrenUnderNode)
    ),
    total: countFutureNodesFromCurrent(tree),
  };
}

/**
 * Get depth levels of future nodes from current position
 *
 * @param tree - The conversation tree
 * @returns Object with depth level counts
 */
export function getFutureNodeDepths(tree: ConversationTree | null): {
  level1: number;
  level2: number;
  level3Plus: number;
  total: number;
} {
  if (!tree) {
    return { level1: 0, level2: 0, level3Plus: 0, total: 0 };
  }

  const currentNode = findCurrentNode(tree.rootNode, tree.currentNodeId);
  if (!currentNode) {
    return { level1: 0, level2: 0, level3Plus: 0, total: 0 };
  }

  const counts = { level1: 0, level2: 0, level3Plus: 0, total: 0 };

  function countByDepth(node: TreeNode, depth: number) {
    if (isFutureStatus(node.status)) {
      counts.total++;
      if (depth === 1) counts.level1++;
      else if (depth === 2) counts.level2++;
      else counts.level3Plus++;
    }

    for (const child of node.children || []) {
      countByDepth(child, depth + 1);
    }
  }

  for (const child of currentNode.children) {
    countByDepth(child, 1);
  }

  return counts;
}
